import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { disableWebPush, readDeviceReport, subscribeWebPush, webPushDeviceStatus } from '../../lib/webPushSubscription';
import { describeDeviceReport, describeReportMeta } from '../../lib/pushReportText';
import { checkWorkerUpdate, localBannerAdvice, readWorkerVersion, showLocalTestBanner, workerVersionLine } from '../../lib/pushWorkerVersion';
import { deviceDisplayName, sendTestPush } from '../../lib/webPushTest';
import { blockedGuidance, devicePlatform, installedAppName, isInstalledApp } from '../../lib/installedApp';
import Switch from '../ui/Switch';
import Button3D from '../ui/Button3D';
import StatusChip from '../ui/StatusChip';
import { useSaveToast } from '../ui/SaveToast';

const STATUS_TEXT = {
    on: 'On (this device is receiving alerts)',
    off: 'Off',
    blocked: 'Blocked by browser settings',
    unsupported: 'Not supported on this browser',
    install: 'iPhone: add to Home Screen first',
};

// variant="card" is the BL-177 Step 2d "This device" card (Settings > My notifications): device name from the user
// agent, a green "Registered to you" chip that only appears when the database read-back finds an enabled row for
// this browser's endpoint and this user, and "Send a test" (pushes to this device only). The default variant is
// unchanged for the client portal and client app.
export default function WebPushDeviceControl({ client, userId, orgId, accountId = null, clientApp = false, variant = 'default' }) {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [testing, setTesting] = useState(false);
    const [testNote, setTestNote] = useState('');
    const [report, setReport] = useState(null);
    const [workerVersion, setWorkerVersion] = useState(null); // null = not asked yet, '' = no answer
    const [localBanner, setLocalBanner] = useState('');
    const toast = useSaveToast();
    const statusId = useId();
    const registrationId = useId();
    const guidanceId = useId();
    const generation = useRef(0);
    const changing = useRef(false);
    const enabled = status === 'on';

    // The push worker reports what it did with the last push on this device (banner shown / suppressed / error).
    const loadReport = useCallback(async () => {
        const value = await readDeviceReport?.(client, { userId, orgId, accountId });
        setReport(value || null);
        return value || null;
    }, [client, userId, orgId, accountId]);
    useEffect(() => { if (variant === 'card' && status === 'on') loadReport(); }, [variant, status, loadReport]);
    // BL-177 Step 2i: which push worker is running on this device. A newer worker file is fetched now and takes over at once.
    useEffect(() => {
        if (variant !== 'card' || status !== 'on') return undefined;
        let active = true;
        (async () => {
            await checkWorkerUpdate?.();
            const version = await readWorkerVersion?.();
            if (active) setWorkerVersion(version || '');
        })();
        return () => { active = false; };
    }, [variant, status]);
    async function showBannerHere() {
        setLocalBanner('Showing a banner…');
        const result = await showLocalTestBanner();
        setLocalBanner(localBannerAdvice(result, { platform: devicePlatform(), browserName: deviceDisplayName().split(' on ')[0] }));
    }

    const readStatus = useCallback(() => webPushDeviceStatus(client, { userId, orgId, accountId }),
        [client, userId, orgId, accountId]);

    useEffect(() => {
        let active = true;
        generation.current += 1;
        changing.current = false;
        async function refresh() {
            if (changing.current || document.visibilityState === 'hidden') return;
            const request = ++generation.current;
            setBusy(true);
            setError('');
            try {
                const value = await readStatus();
                if (active && request === generation.current) setStatus(value);
            } catch (failure) {
                if (active && request === generation.current) {
                    setStatus(null);
                    setError(failure.message);
                }
            } finally {
                if (active && request === generation.current) setBusy(false);
            }
        }
        setStatus(null);
        refresh();
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            active = false;
            generation.current += 1;
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [readStatus]);

    async function toggle() {
        if (busy || changing.current) return;
        const request = ++generation.current;
        changing.current = true;
        setBusy(true);
        setError('');
        const wanted = enabled ? 'off' : 'on';
        let actionError = '';
        try {
            if (enabled) await disableWebPush(client, { userId, orgId, accountId });
            else await subscribeWebPush(client, { userId, orgId, accountId, clientApp });
        } catch (failure) {
            actionError = failure.message;
        }
        try {
            const value = await readStatus();
            if (request === generation.current) {
                setStatus(value);
                // A registration failure is a failed save: red toast, and the switch stays wherever the
                // database read-back says it is (never "on" without an enabled row for this browser).
                if (actionError) toast.failed(actionError);
                else if (value === wanted) toast.saved();
                else toast.failed('This device could not be confirmed. Please try again.');
            }
        } catch (failure) {
            if (request === generation.current) {
                setStatus(null);
                if (actionError) toast.failed(actionError);
                setError(failure.message);
            }
        } finally {
            if (request === generation.current) {
                changing.current = false;
                setBusy(false);
            }
        }
    }

    async function sendTest() {
        if (testing || status !== 'on') return;
        setTesting(true);
        setTestNote('');
        try {
            await sendTestPush(client);
            setTestNote('Test sent. It should appear on this device in a few seconds.');
            // the worker reports back within moments: look for it a couple of times
            [2500, 6000].forEach((delay) => setTimeout(() => { loadReport(); }, delay));
        } catch (failure) {
            toast.failed(failure.message || 'The test could not be sent.');
        } finally {
            setTesting(false);
        }
    }

    // An installed app (Dock app, Home Screen app) has no browser settings: the block lives in the system's Notifications settings.
    const statusText = (value) => (value === 'blocked' && isInstalledApp() ? 'Blocked in system settings' : STATUS_TEXT[value]);
    const guidance = status === 'blocked'
        ? blockedGuidance({
            installed: isInstalledApp(),
            platform: devicePlatform(),
            appName: installedAppName(),
            host: globalThis.location?.hostname || '',
        })
        : status === 'install'
            ? 'Open this site in Safari, tap Share, choose Add to Home Screen, and tap Add. Open the new Home Screen app, then turn on alerts here.'
            : '';
    if (variant === 'card') {
        const titleId = `${statusId}-title`;
        return (
            <section aria-labelledby={titleId} className="ia-d2-card flex flex-col gap-3" data-testid="this-device-card">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h3 id={titleId} className="text-base font-bold">This device</h3>
                        <p className="text-sm">{deviceDisplayName()}</p>
                    </div>
                    <Switch
                        label="Alerts on this device"
                        checked={enabled}
                        describedBy={`${statusId}${guidance ? ` ${guidanceId}` : ''}`}
                        busy={busy}
                        disabled={busy || !['on', 'off'].includes(status) || !userId || !orgId}
                        onChange={toggle}
                    />
                </div>
                <p id={statusId} role="status" data-testid="device-registration" className="text-sm">
                    {busy && 'Checking this device...'}
                    {!busy && status === 'on' && (
                        <StatusChip tone="green" icon={<svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16"><path d="M3 8.5l3 3L13 4.5" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" /></svg>}>Registered to you</StatusChip>
                    )}
                    {!busy && status === 'off' && 'Not registered. Turn the switch on to get alerts here.'}
                    {!busy && status && !['on', 'off'].includes(status) && statusText(status)}
                    {!busy && !status && 'Device notification status could not be loaded.'}
                </p>
                {guidance && <p id={guidanceId} className="text-sm">{guidance}</p>}
                {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
                {status === 'on' && workerVersion !== null && (
                    <p className="text-xs" data-testid="push-worker-version">{workerVersionLine(workerVersion)}</p>
                )}
                {status === 'on' && describeDeviceReport(report) && (
                    <p className="text-sm" data-testid="device-last-report">
                        <span className="block font-semibold">{describeDeviceReport(report)}</span>
                        <span className="block">{describeReportMeta(report)}</span>
                    </p>
                )}
                <div className="flex flex-wrap items-center gap-3">
                    <Button3D variant="pill" onClick={sendTest} disabled={busy || testing || status !== 'on'} aria-busy={testing || undefined}>
                        {testing ? 'Sending...' : 'Send a test'}
                    </Button3D>
                    {testNote && <span role="status" className="text-sm">{testNote}</span>}
                    <Button3D variant="pill" onClick={showBannerHere} disabled={busy || status !== 'on'}>Show a banner here</Button3D>
                </div>
                {localBanner && <p role="status" className="text-sm" data-testid="local-banner-result">{localBanner}</p>}
            </section>
        );
    }
    return (
        <section aria-label="Device notifications" className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4">
            <div className="flex items-center justify-between gap-4">
                <span className="font-semibold text-[var(--ink)]">Alerts on this device</span>
                <Switch
                    label="Alerts on this device"
                    checked={enabled}
                    describedBy={`${statusId}${guidance ? ` ${guidanceId}` : ''}`}
                    busy={busy}
                    disabled={busy || !['on', 'off'].includes(status) || !userId || !orgId}
                    onChange={toggle}
                />
            </div>
            <p id={statusId} role="status" className="mt-2 text-sm text-[var(--ink-muted)]">
                {busy ? 'Checking device notification status...' : statusText(status) || 'Device notification status could not be loaded.'}
            </p>
            {!busy && status && (
                <p id={registrationId} data-testid="device-registration" className="mt-1 text-sm text-[var(--ink-muted)]">
                    {status === 'on' ? 'This device: registered to you' : 'This device: not registered'}
                </p>
            )}
            {guidance && <p id={guidanceId} className="mt-1 text-sm text-[var(--ink-muted)]">{guidance}</p>}
            {error && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
        </section>
    );
}
